import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, isTaskOverdue } from '../../core/helpers';
import { buildMonthlySeries, buildWeeklySeries, buildClientBillingTable, metricsFromInvoices } from '../../core/billing-chart-data';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, DEFAULT_DOC_TEMPLATES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { Icon, InlineIcon } from '../../components/Icon';
import { DrillPanel } from './AdminDashboard';
import { apiGet } from '../../core/auth';
import { PeriodFilter, usePeriodRange, inPeriod } from '../../components/PeriodFilter';

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function AnalyticsPage() {
  const { clients, invoices, tasks, employees } = useApp();
  const [analytics, setAnalytics] = useState(null);
  const [drill, setDrill] = useState(null);
  const [period, setPeriod] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const range = usePeriodRange(period, customFrom, customTo);
  const now = new Date();
  const year = analytics?.year || now.getFullYear();
  useEffect(() => {
    let active = true;
    apiGet("/dashboard/analytics")
      .then(res => { if (active) setAnalytics(res?.data || null); })
      .catch(() => { if (active) setAnalytics(null); });
    return () => { active = false; };
  }, []);

  const liveInvoices = invoices.filter(i => i.status !== "void" && inPeriod(i.date, range));
  const liveSummary = {
    totalRevenue: liveInvoices.reduce((s, i) => s + (i.total || 0), 0),
    totalCollected: liveInvoices.reduce((s, i) => s + (i.paid || 0), 0),
    outstanding: liveInvoices.reduce((s, i) => s + (i.pending || 0), 0),
    avgPerClient: clients.length ? Math.round(liveInvoices.reduce((s, i) => s + (i.total || 0), 0) / clients.length) : 0,
  };
  const summary = analytics?.summary || liveSummary;
  const liveMonthlyData = buildMonthlySeries(
    invoices.filter(i => i.status !== 'void'),
    year,
    MONTHS,
  ).map((m, idx) => ({
    month: m.label,
    clients: new Set(
      invoices.filter(inv => {
        if (inv.status === 'void') return false;
        const d = new Date(inv.date || 0);
        return d.getMonth() === idx && d.getFullYear() === year;
      }).map(inv => inv.clientId).filter(Boolean),
    ).size,
    billing: m.billing,
    collected: m.collected,
  }));
  const chartIsWeekly = period === 'week' || period === 'today';
  const weeklyChart = buildWeeklySeries(liveInvoices, period === 'today' ? 4 : 12);
  const monthlyChart = liveMonthlyData;
  const chartData = chartIsWeekly
    ? weeklyChart.map(w => ({ month: w.label, billing: w.billing, collected: w.collected, clients: w.count }))
    : monthlyChart;
  const monthlyData = chartData;
  const employeePerformance = analytics?.employeePerformance?.length
    ? analytics.employeePerformance
    : employees.map(emp => {
      const empTasks = tasks.filter(t => t.assignedTo === emp.id);
      const completed = empTasks.filter(t => t.status === "completed").length;
      const pending = empTasks.filter(t => t.status !== "completed" && t.status !== "cancelled").length;
      const overdue = empTasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== "completed").length;
      const empClients = clients.filter(c => c.assignedTo === emp.id).length;
      return { ...emp, clients: empClients, totalTasks: empTasks.length, completed, pending, overdue, efficiency: empTasks.length ? Math.round(completed / empTasks.length * 100) : 0 };
    });
  const maxBilling = Math.max(...monthlyData.map(d => d.billing));

  return (
    <>
      <PeriodFilter period={period} setPeriod={setPeriod} customFrom={customFrom} setCustomFrom={setCustomFrom} customTo={customTo} setCustomTo={setCustomTo} />
      <div className="stat-cards" style={{ marginBottom: 22 }}>
        {[
          { label: "Total Revenue",    val: INR(summary.totalRevenue),  color: "var(--ink)", drill: "billing" },
          { label: "Total Collected",  val: INR(summary.totalCollected),     color: "var(--green)", drill: "collected" },
          { label: "Outstanding",      val: INR(summary.outstanding),       color: "var(--red)", drill: "pending" },
          { label: "Avg per Client",   val: INR(summary.avgPerClient), color: "var(--blue)", drill: "clients" },
        ].map(s => (
          <div key={s.label} className="stat-box" onClick={() => setDrill({ type: s.drill, title: s.label })} style={{ cursor: "pointer" }}>
            <div className="stat-lbl">{s.label}</div>
            <div className="stat-val" style={{ color: s.color, fontSize: 20 }}>{s.val}</div>
            <div className="stat-drill">Click to expand →</div>
          </div>
        ))}
      </div>

      {drill && <DrillPanel type={drill.type} title={drill.title} range={range} onClose={() => setDrill(null)} />}

      {/* Monthly billing chart */}
      <div className="card analytics-chart" style={{ marginBottom: 20 }}>
        <div className="card-head"><div className="card-title">{chartIsWeekly ? 'Weekly' : 'Monthly'} Billing &amp; Collections ({range.label})</div></div>
        <div className="card-body">
          <div className="analytics-chart-legend">
            <div><i className="analytics-chart-billed" />Billed</div>
            <div><i className="analytics-chart-collected" />Collected</div>
          </div>
          <div className="analytics-chart-bars">
            {monthlyData.map(d => (
              <div key={d.month} className="analytics-chart-month">
                <div className="analytics-chart-pair">
                  <div title={`Billed: ${INR(d.billing)}`} className="analytics-chart-bar analytics-chart-billed" style={{ height: maxBilling > 0 ? `${Math.max(3, Math.round(d.billing / maxBilling * 138))}px` : "3px" }} />
                  <div title={`Collected: ${INR(d.collected)}`} className="analytics-chart-bar analytics-chart-collected" style={{ height: maxBilling > 0 ? `${Math.max(3, Math.round(d.collected / maxBilling * 138))}px` : "3px" }} />
                </div>
                <div className="analytics-chart-label">{d.month}</div>
                <div className="analytics-chart-clients">{d.clients ? `${d.clients} client${d.clients > 1 ? "s" : ""}` : "—"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Employee wise */}
      <div className="card">
        <div className="card-head"><div className="card-title">Employee Performance</div></div>
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>Employee</th><th>Clients</th><th>Total Tasks</th><th>Completed</th><th>Pending</th><th>Overdue</th><th>Efficiency</th></tr></thead>
            <tbody>
              {employeePerformance.map(emp => {
                const eff = emp.efficiency || 0;
                return (
                  <tr key={emp.id}>
                    <td><div style={{ display: "flex", alignItems: "center", gap: 8 }}><div className="av av-sm" style={{ background: emp.color }}>{emp.avatar}</div><span style={{ fontWeight: 600 }}>{emp.name}</span></div></td>
                    <td>{emp.clients}</td>
                    <td>{emp.totalTasks}</td>
                    <td style={{ color: "var(--green)", fontWeight: 600 }}>{emp.completed}</td>
                    <td style={{ color: "var(--orange)", fontWeight: 600 }}>{emp.pending}</td>
                    <td style={{ color: emp.overdue > 0 ? "var(--red)" : "var(--muted)", fontWeight: 600 }}>{emp.overdue}</td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div className="prog-bg" style={{ width: 80 }}>
                          <div className="prog-fill" style={{ width: eff + "%", background: eff > 80 ? "var(--green)" : eff > 50 ? "var(--blue)" : "var(--orange)" }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{eff}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// #39 Reusable module filters (search / client / date range)
// ═══════════════════════════════════════════════════════════════════════

function BillingSummaryPage() {
  const { invoices, clients } = useApp();
  const [period, setPeriod] = useState("year");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [granularity, setGranularity] = useState("monthly");
  const range = usePeriodRange(period, customFrom, customTo);
  const now = new Date();

  const filteredInvoices = invoices.filter(inv =>
    inv.status !== "void" && inPeriod(inv.date, range)
  );

  const chartYear = range.start ? range.start.getFullYear() : now.getFullYear();
  const monthlyData = buildMonthlySeries(filteredInvoices, chartYear, MONTHS).map(m => ({
    month: m.label,
    billing: m.billing,
    collected: m.collected,
    govtFees: m.govtFees,
    gst: m.gst,
    dsc: m.dsc,
    gross: m.gross,
    count: m.count,
  }));

  const weeklyData = buildWeeklySeries(filteredInvoices, period === "today" ? 4 : 12).map(w => ({
    month: w.label,
    billing: w.billing,
    collected: w.collected,
    govtFees: w.govtFees,
    gst: w.gst,
    dsc: w.dsc,
    gross: w.gross,
    count: w.count,
  }));

  const chartData = granularity === "weekly" ? weeklyData : monthlyData;
  const periodMetrics = metricsFromInvoices(filteredInvoices);
  const ytdBilling = periodMetrics.billing;
  const ytdCollected = periodMetrics.collected;
  const ytdGross = periodMetrics.gross;
  const clientRows = buildClientBillingTable(filteredInvoices, clients);
  const maxBilling = Math.max(...chartData.map(m => m.billing), 1);

  return (
    <>
      <PeriodFilter period={period} setPeriod={setPeriod} customFrom={customFrom} setCustomFrom={setCustomFrom} customTo={customTo} setCustomTo={setCustomTo} />
      <div style={{ display:"flex",gap:10,alignItems:"center",marginBottom:20,flexWrap:"wrap" }}>
        <div className="chips" style={{ margin:0 }}>
          {["monthly","weekly"].map(p=><div key={p} className={`chip ${granularity===p?"on":""}`} onClick={()=>setGranularity(p)} style={{ textTransform:"capitalize" }}>{p} chart</div>)}
        </div>
        <span style={{ fontSize:12,color:"var(--muted)" }}>{range.label} · {filteredInvoices.length} invoices</span>
      </div>

      {/* Period summary */}
      <div className="stat-cards" style={{ marginBottom:22 }}>
        {[
          ["Total Billed",    INR(ytdBilling),   "var(--navy)",  range.label],
          ["Collected",       INR(ytdCollected), "var(--green)", `${Math.round(ytdCollected/Math.max(ytdBilling,1)*100)}% collection`],
          ["Gross Margin",    INR(ytdGross),     "var(--gold-dk)", `Excl. govt fees & GST`],
          ["Invoices",        filteredInvoices.length, "var(--blue)", `${clientRows.length} clients`],
        ].map(([l,v,c,n])=>(
          <div key={l} className="stat-box" style={{ cursor:"default" }}>
            <div className="stat-lbl">{l}</div>
            <div className="stat-val" style={{ color:c,fontSize:22 }}>{v}</div>
            <div className="stat-note">{n}</div>
          </div>
        ))}
      </div>

      {/* Billing chart */}
      <div className="card card-gold" style={{ marginBottom:20 }}>
        <div className="card-head"><div className="card-title">{granularity === "weekly" ? "Weekly" : "Monthly"} Billing & Collections — {range.label}</div></div>
        <div className="card-body">
          <div style={{ display:"flex",gap:16,alignItems:"center",marginBottom:16,fontSize:12 }}>
            {[["Billed","var(--navy)"],["Collected","var(--green)"],["Gross Margin","var(--gold-dk)"]].map(([l,c])=>(
              <div key={l} style={{ display:"flex",alignItems:"center",gap:5 }}>
                <div style={{ width:10,height:10,borderRadius:3,background:c }}/>
                <span style={{ color:"var(--muted)" }}>{l}</span>
              </div>
            ))}
          </div>
          <div className="billing-chart-scroll">
            <div className="billing-chart-bars" style={{ display:"flex",gap:6,alignItems:"flex-end",height:180,minWidth:granularity==="weekly"?480:undefined }}>
            {chartData.map((d,i)=>(
              <div key={d.month + i} style={{ flex:1,minWidth:granularity==="weekly"?36:undefined,display:"flex",flexDirection:"column",alignItems:"center",gap:3 }}>
                <div style={{ width:"100%",display:"flex",gap:2,alignItems:"flex-end",height:150 }}>
                  <div title={`Billed: ${INR(d.billing)}`} style={{ flex:1,background:"var(--navy)",opacity:.8,borderRadius:"3px 3px 0 0",height:maxBilling>0?`${Math.round(d.billing/maxBilling*140)}px`:"2px",minHeight:2,cursor:"pointer",transition:".2s" }}/>
                  <div title={`Collected: ${INR(d.collected)}`} style={{ flex:1,background:"var(--green)",opacity:.8,borderRadius:"3px 3px 0 0",height:maxBilling>0?`${Math.round(d.collected/maxBilling*140)}px`:"2px",minHeight:2 }}/>
                  <div title={`Gross: ${INR(d.gross)}`} style={{ flex:1,background:"var(--gold)",opacity:.8,borderRadius:"3px 3px 0 0",height:maxBilling>0?`${Math.round(Math.max(0,d.gross)/maxBilling*140)}px`:"2px",minHeight:2 }}/>
                </div>
                <div style={{ fontSize:9,color:granularity==="monthly"&&i===now.getMonth()&&period==="year"?"var(--navy)":"var(--muted)",fontWeight:granularity==="monthly"&&i===now.getMonth()&&period==="year"?700:400 }}>{d.month}</div>
              </div>
            ))}
            </div>
          </div>
        </div>
      </div>

      {/* Detail table */}
      <div className="card">
        <div className="card-head"><div className="card-title">{granularity === "weekly" ? "Weekly" : "Monthly"} Billing Breakdown — {range.label}</div></div>
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>{granularity === "weekly" ? "Week" : "Month"}</th><th>Invoices</th><th>Total Billed</th><th>Govt Fees</th><th>GST Collected</th><th>DSC Amount</th><th>Gross Margin</th><th>Collected</th><th>Outstanding</th></tr></thead>
            <tbody>
              {(granularity === "weekly"
                ? chartData.filter(m => m.billing > 0 || m.count > 0).slice(-8)
                : chartData.filter(m => m.billing > 0 || m.count > 0)
              ).map((m,i)=>(
                <tr key={m.month + i}>
                  <td style={{ fontWeight:600,color:"var(--navy)" }}>
                    {granularity === "weekly" ? m.month : `${m.month} ${chartYear}`}
                  </td>
                  <td>{m.count}</td>
                  <td style={{ fontWeight:700 }}>{INR(m.billing)}</td>
                  <td style={{ color:"var(--muted)" }}>{INR(m.govtFees)}</td>
                  <td style={{ color:"var(--orange)" }}>{INR(m.gst)}</td>
                  <td style={{ color:"var(--blue)" }}>{INR(m.dsc)}</td>
                  <td style={{ color:"var(--gold-dk)",fontWeight:700 }}>{INR(m.gross)}</td>
                  <td style={{ color:"var(--green)",fontWeight:600 }}>{INR(m.collected)}</td>
                  <td style={{ color:m.billing-m.collected>0?"var(--red)":"var(--muted)",fontWeight:600 }}>{INR(m.billing-m.collected)}</td>
                </tr>
              ))}
              <tr className="tbl-summary" style={{ color:"#fff" }}>
                <td style={{ fontWeight:700,color:"#fff" }}>Period Total</td>
                <td style={{ color:"#fff" }}>{filteredInvoices.length}</td>
                <td style={{ fontWeight:700,color:"#C9A14A" }}>{INR(ytdBilling)}</td>
                <td style={{ color:"rgba(255,255,255,.6)" }}>{INR(periodMetrics.govtFees)}</td>
                <td style={{ color:"rgba(255,255,255,.6)" }}>{INR(periodMetrics.gst)}</td>
                <td style={{ color:"rgba(255,255,255,.6)" }}>{INR(periodMetrics.dsc)}</td>
                <td style={{ fontWeight:700,color:"#C9A14A" }}>{INR(ytdGross)}</td>
                <td style={{ color:"#86EFAC",fontWeight:700 }}>{INR(ytdCollected)}</td>
                <td style={{ color:"#FCA5A5",fontWeight:700 }}>{INR(ytdBilling-ytdCollected)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Client-wise billing */}
      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-head"><div className="card-title">Client-wise Billing — {range.label}</div></div>
        {clientRows.length === 0 ? (
          <div className="empty" style={{ padding: 24 }}><div>No invoices in this period</div></div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr><th>Client</th><th>Invoices</th><th>Billed</th><th>Collected</th><th>Outstanding</th><th>Collection %</th></tr>
              </thead>
              <tbody>
                {clientRows.map(row => (
                  <tr key={row.clientId}>
                    <td style={{ fontWeight: 600 }}>{row.clientName}</td>
                    <td>{row.count}</td>
                    <td style={{ fontWeight: 700 }}>{INR(row.billed)}</td>
                    <td style={{ color: "var(--green)", fontWeight: 600 }}>{INR(row.collected)}</td>
                    <td style={{ color: row.outstanding > 0 ? "var(--red)" : "var(--muted)", fontWeight: 600 }}>{INR(row.outstanding)}</td>
                    <td>{row.billed ? Math.round(row.collected / row.billed * 100) : 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// BATCH B — RECURRING TASKS ENGINE
// ═══════════════════════════════════════════════════════════════════════


function EmployeeAnalyticsPage() {
  const { employees, tasks, clients, user } = useApp();
  const isEmployee = user?.role === "employee";
  const [period, setPeriod] = useState("monthly");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [selEmp, setSelEmp] = useState(isEmployee ? user.id : "all");
  const [drill, setDrill] = useState(null);
  const now = new Date();

  const presetMap = { daily: "today", weekly: "week", monthly: "month", yearly: "year", all: "all", custom: "custom" };
  const range = usePeriodRange(presetMap[period] || period, customFrom, customTo);

  const empStats = employees.map(emp => {
    const allTasks     = tasks.filter(t => t.assignedTo === emp.id || (clients.some(c => c.id === t.clientId && c.assignedTo === emp.id)));
    const periodTasks  = allTasks.filter(t => t.completedDate && inPeriod(t.completedDate, range));
    const overdue      = allTasks.filter(t=>t.dueDate&&new Date(t.dueDate)<now&&t.status!=="completed");
    const pending      = allTasks.filter(t=>t.status!=="completed"&&t.status!=="cancelled");
    const empClients   = clients.filter(c=>c.assignedTo===emp.id);
    // Overdue ageing
    const aged12  = overdue.filter(t=>{ const d=new Date(t.dueDate); return (now-d)/(1000*3600)>=12 && (now-d)/(1000*3600)<24; });
    const aged24  = overdue.filter(t=>{ const d=new Date(t.dueDate); return (now-d)/(1000*3600)>=24 && (now-d)/(1000*3600)<48; });
    const aged48  = overdue.filter(t=>{ const d=new Date(t.dueDate); return (now-d)/(1000*3600)>=48 && (now-d)/(1000*3600)<72; });
    const aged72p = overdue.filter(t=>{ const d=new Date(t.dueDate); return (now-d)/(1000*3600)>=72; });
    const pct = allTasks.length ? Math.round(allTasks.filter(t=>t.status==="completed").length/allTasks.length*100) : 0;
    return { ...emp, allTasks:allTasks.length, periodCompleted:periodTasks.length, overdue:overdue.length, pending:pending.length, clients:empClients.length, pct, aged12:aged12.length, aged24:aged24.length, aged48:aged48.length, aged72p:aged72p.length };
  });

  const filtered = selEmp==="all" ? empStats : empStats.filter(e=>e.id===selEmp);

  return (
    <>
      <div style={{ display:"flex",gap:12,marginBottom:20,alignItems:"center",flexWrap:"wrap" }}>
        <PeriodFilter period={presetMap[period] || period} setPeriod={p => {
          const rev = { today: "daily", week: "weekly", month: "monthly", year: "yearly", all: "all", custom: "custom" };
          setPeriod(rev[p] || p);
        }} customFrom={customFrom} setCustomFrom={setCustomFrom} customTo={customTo} setCustomTo={setCustomTo} style={{ marginBottom: 0, flex: 1 }} />
        <select className="f-select" style={{ width:200 }} value={selEmp} onChange={e=>setSelEmp(e.target.value)} disabled={isEmployee}>
          {!isEmployee && <option value="all">All Employees</option>}
          {(isEmployee ? employees.filter(e => e.id === user.id) : employees).map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </div>

      {/* Summary cards */}
      <div className="stat-cards" style={{ marginBottom:20 }}>
        {[
          ["Completed "+range.label, empStats.reduce((s,e)=>s+e.periodCompleted,0), "var(--green)", "completed_tasks", null],
          ["Total Pending",          empStats.reduce((s,e)=>s+e.pending,0),        "var(--orange)", "pending_tasks", null],
          ["Overdue Tasks",          empStats.reduce((s,e)=>s+e.overdue,0),        "var(--red)", "overdue_tasks", null],
          ["72hr+ Overdue",          empStats.reduce((s,e)=>s+e.aged72p,0),        "#7C3AED", "emp_overdue_72", null],
        ].map(([l,v,c, drillType, bucket])=>(
          <div key={l} className="stat-box" style={{ cursor:"pointer" }} onClick={() => setDrill({ type: drillType, title: l, overdueBucket: bucket })}>
            <div className="stat-lbl">{l}</div>
            <div className="stat-val" style={{ color:c,fontSize:22 }}>{v}</div>
            <div className="stat-drill">Click to expand →</div>
          </div>
        ))}
      </div>

      {drill && (
        <DrillPanel
          type={drill.type}
          title={drill.title}
          range={range}
          empId={drill.empId}
          overdueBucket={drill.overdueBucket}
          onClose={() => setDrill(null)}
        />
      )}

      {/* Per employee breakdown */}
      {filtered.map(emp=>(
        <div key={emp.id} className="card" style={{ marginBottom:16 }}>
          <div className="card-head">
            <div style={{ display:"flex",alignItems:"center",gap:12 }}>
              <div className="av av-lg" style={{ background:emp.color }}>{emp.avatar}</div>
              <div>
                <div className="card-title">{emp.name}</div>
                <div style={{ fontSize:12,color:"var(--muted)" }}>{emp.role} · {emp.clients} clients · {emp.allTasks} total tasks</div>
              </div>
            </div>
            <div style={{ textAlign:"right" }}>
              <div style={{ fontFamily:"'Outfit',sans-serif",fontSize:28,fontWeight:700,color:emp.pct>=80?"var(--green)":emp.pct>=50?"var(--gold-dk)":"var(--red)" }}>{emp.pct}%</div>
              <div style={{ fontSize:11,color:"var(--muted)" }}>completion rate</div>
            </div>
          </div>
          <div className="card-body">
            {/* Stats row */}
            <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16 }}>
              {[
                ["Completed "+range.label, emp.periodCompleted, "var(--green)",  "#F0FDF4", "completed_tasks"],
                ["Pending",                emp.pending,         "var(--orange)", "#FFF8EC", "pending_tasks"],
                ["Overdue",                emp.overdue,         "var(--red)",    "#FEF2F2", "overdue_tasks"],
                ["Avg per day",            Math.round(emp.allTasks/30)||0, "var(--blue)", "#EEF3FB", null],
              ].map(([l,v,c,bg, drillType])=>(
                <div key={l} style={{ textAlign:"center",padding:"12px 8px",borderRadius:10,background:bg, cursor: drillType ? "pointer" : "default" }}
                  onClick={drillType ? () => setDrill({ type: drillType, title: `${emp.name} — ${l}`, empId: emp.id }) : undefined}>
                  <div style={{ fontSize:20,fontWeight:700,color:c }}>{v}</div>
                  <div style={{ fontSize:11,color:"var(--muted)",marginTop:2 }}>{l}</div>
                </div>
              ))}
            </div>

            {/* Overdue ageing breakdown */}
            {emp.overdue > 0 && (
              <div>
                <div style={{ fontSize:12,fontWeight:700,marginBottom:8,color:"var(--navy)" }}>Overdue Ageing</div>
                <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8 }}>
                  {[
                    ["12–24 hrs", emp.aged12, "#FFF8EC","#B45309", "12-24"],
                    ["24–48 hrs", emp.aged24, "#FEF2F2","#DC2626", "24-48"],
                    ["48–72 hrs", emp.aged48, "#FFF1F2","#BE123C", "48-72"],
                    ["72 hrs+",   emp.aged72p,"#FDF2F8","#9D174D", "72+"],
                  ].map(([l,v,bg,c, bucket])=>(
                    <div key={l} style={{ padding:"10px",borderRadius:8,background:bg,textAlign:"center",border:`1px solid ${c}30`, cursor:"pointer" }}
                      onClick={() => setDrill({ type: "overdue_tasks", title: `${emp.name} — ${l} overdue`, empId: emp.id, overdueBucket: bucket })}>
                      <div style={{ fontSize:18,fontWeight:700,color:c }}>{v}</div>
                      <div style={{ fontSize:10,color:c,fontWeight:600,marginTop:2 }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Progress bar */}
            <div style={{ marginTop:12 }}>
              <div style={{ display:"flex",justifyContent:"space-between",marginBottom:5,fontSize:11 }}>
                <span style={{ color:"var(--muted)" }}>Overall completion</span>
                <span style={{ fontWeight:700 }}>{emp.pct}%</span>
              </div>
              <div className="prog-bg">
                <div className="prog-fill" style={{ width:emp.pct+"%",background:emp.pct>=80?"var(--green)":emp.pct>=50?"var(--gold)":"var(--red)" }}/>
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MANAGER DASHBOARD — with overdue ageing focus
// ═══════════════════════════════════════════════════════════════════════



export { AnalyticsPage, BillingSummaryPage, EmployeeAnalyticsPage };
